import { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, ChevronUp, Settings, X, RefreshCw } from 'lucide-react';
import { VariableType } from '@/lib/calculator';
import { useCalculatorStore, VARIABLE_ORDER } from '@/store/calculator';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

interface MultiSelectDropdownProps {
  label: string;
  selectedVariables: VariableType[];
  excludeVariables?: VariableType[];
  maxSelections?: number; // Undefined means no limit
  onAddVariable: (type: VariableType) => void;
  onRemoveVariable: (index: number) => void;
  onEditStep?: (type: VariableType) => void; // New prop for step editing
}

export function MultiSelectDropdown({
  label,
  selectedVariables,
  excludeVariables = [],
  maxSelections,
  onAddVariable,
  onRemoveVariable,
  onEditStep
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { target, values, resetStepsToDefaults } = useCalculatorStore();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Get all available variables that aren't already selected or excluded
  const getAvailableVariables = () => {
    return VARIABLE_ORDER
      .map(type => values[type])
      .filter(variable => {
        // Don't include the target
        if (variable.type === target) return false;
        
        // Don't include already selected variables
        if (selectedVariables.includes(variable.type)) return false;
        
        // Don't include explicitly excluded variables
        if (excludeVariables.includes(variable.type)) return false;
        
        return true;
      });
  };
  
  // Create a map of excluded variables to their reasons
  const getVariableExclusionReason = (type: VariableType): string | null => {
    if (type === target) return "Currently set as target";
    if (excludeVariables.includes(type)) {
      // Check if it's in use in the other dropdown (primary or secondary)
      const isPrimary = label.includes("Primary");
      return isPrimary ? "In use for columns" : "In use for rows";
    }
    return null;
  };

  const availableVariables = getAvailableVariables();
  const isMaxReached = maxSelections !== undefined && selectedVariables.length >= maxSelections;
  const selectionCount = selectedVariables.length;

  // Helper to get default variables based on current target
  const getDefaultVariablesForTarget = (): VariableType[] => {
    switch (target) {
      case 'price':
        return ['income', 'date'];
      case 'payment':
        return ['interest'];
      case 'date':
        return ['savings'];
      case 'savings':
        return ['price'];
      default:
        return [];
    }
  };

  // Enhanced handler for resetting all step defaults
  // but keeping at least one variable selected to maintain results
  const handleResetDefaults = () => {
    // First reset all step sizes and directions
    resetStepsToDefaults();
    
    // Get the default variables for the current target
    const defaultVars = getDefaultVariablesForTarget();
    
    // For primary variables (rows)
    if (label.includes("Primary")) {
      // Keep only the first selected variable if it exists
      if (selectedVariables.length > 1) {
        // Get the index of all variables except the first one
        const indicesToRemove: number[] = [];
        for (let i = 1; i < selectedVariables.length; i++) {
          indicesToRemove.push(i);
        }
        
        // Remove in reverse order to avoid index shifting problems
        for (let i = indicesToRemove.length - 1; i >= 0; i--) {
          onRemoveVariable(indicesToRemove[i]);
        }
      } 
      // If no variables are selected, add the first default
      else if (selectedVariables.length === 0 && defaultVars.length > 0) {
        onAddVariable(defaultVars[0]);
      }
    } 
    // For secondary variables (columns)
    else {
      // Keep only the first selected variable if it exists
      if (selectedVariables.length > 1) {
        // Get the index of all variables except the first one
        const indicesToRemove: number[] = [];
        for (let i = 1; i < selectedVariables.length; i++) {
          indicesToRemove.push(i);
        }
        
        // Remove in reverse order to avoid index shifting problems
        for (let i = indicesToRemove.length - 1; i >= 0; i--) {
          onRemoveVariable(indicesToRemove[i]);
        }
      }
      // No need to add a default for secondary if empty - that's valid in the UI
    }
  };

  return (
    <div className="relative mb-4" ref={dropdownRef}>
      <div className="mb-2 text-sm font-medium text-gray-700">
        <span>{label}</span>
      </div>
      
      {/* Closed dropdown header */}
      <button
        className="w-full flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-gray-900">
          {selectionCount === 0 
            ? 'Select variables to compare' 
            : `${selectionCount} variable${selectionCount !== 1 ? 's' : ''} selected`}
        </span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
      </button>
      
      {/* Dropdown panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          >
            {/* Panel header with reset button */}
            <div className="p-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-medium text-gray-900">Select variables to compare</h3>
              {(selectedVariables.length > 0 || availableVariables.length > 0) && (
                <button
                  className="text-xs text-primary hover:text-primary-dark flex items-center gap-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResetDefaults();
                  }}
                  title="Reset all variables to defaults">
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Reset</span>
                </button>
              )}
            </div>
            
            {/* Selected variables (at top) */}
            {selectedVariables.map((varType, index) => {
              return (
                <div 
                  key={`selected-${varType}`}
                  className="flex items-center justify-between p-3 bg-primary/5 border-b border-gray-100 hover:bg-primary/10"
                >
                  <div className="flex items-center">
                    <div 
                      className="w-5 h-5 flex items-center justify-center rounded border border-primary bg-white mr-3 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveVariable(index);
                      }}
                    >
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-gray-900">{values[varType].label}</span>
                  </div>
                  
                  <div className="flex items-center">
                    {/* Add gear icon for step editing */}
                    {onEditStep && (
                      <button
                        className="text-gray-500 hover:text-primary mr-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditStep(varType);
                        }}
                        title={`Edit variable: ${values[varType].label}`}
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      className="text-gray-500 hover:text-gray-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveVariable(index);
                      }}
                    >
                      <span className="sr-only">Remove</span>
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              );
            })}
            
            {/* Available variables */}
            {availableVariables.map(variable => (
              <button
                key={`available-${variable.type}`}
                className={cn(
                  "w-full flex items-center p-3 hover:bg-gray-50 text-left",
                  isMaxReached && "opacity-50 cursor-not-allowed"
                )}
                onClick={() => {
                  if (!isMaxReached) {
                    onAddVariable(variable.type);
                    // Removed setIsOpen(false) to keep dropdown open after selection
                  }
                }}
                disabled={isMaxReached}
              >
                <div 
                  className="w-5 h-5 flex items-center justify-center rounded border border-gray-300 mr-3 cursor-pointer"
                  onClick={(e) => {
                    if (!isMaxReached) {
                      e.stopPropagation();
                      onAddVariable(variable.type);
                      // Removed setIsOpen(false) to keep dropdown open after selection
                    }
                  }}
                >
                  {/* Empty checkbox */}
                </div>
                <span className="text-gray-900">{values[variable.type].label}</span>
              </button>
            ))}
            
            {/* Excluded variables that aren't available but we want to show why */}
            {VARIABLE_ORDER
              .filter(type => 
                type !== target && 
                !selectedVariables.includes(type) &&
                excludeVariables.includes(type)
              )
              .map(type => {
                const reason = getVariableExclusionReason(type);
                if (!reason) return null;
                
                return (
                  <div 
                    key={`excluded-${type}`}
                    className="w-full flex items-center p-3 text-left text-gray-400 cursor-not-allowed"
                  >
                    <div className="w-5 h-5 flex items-center justify-center rounded border border-gray-200 mr-3">
                      {/* Empty checkbox */}
                    </div>
                    <span className="text-gray-400">{values[type].label}</span>
                    <span className="ml-2 text-xs text-gray-400 italic">({reason})</span>
                  </div>
                );
              })}
            
            {/* Select all available variables button */}
            {availableVariables.length > 1 && !isMaxReached && (
              <button
                key="select-all"
                className="w-full text-left px-4 py-3 border-t border-gray-100 text-primary hover:bg-gray-50"
                onClick={() => {
                  const availableVariablesList = getAvailableVariables();
                  availableVariablesList.forEach(variable => onAddVariable(variable.type));
                  // Removed setIsOpen(false) to keep dropdown open after selection
                }}
              >
                Select all available inputs
              </button>
            )}
            
            {/* Reset steps to defaults button - removed since we're adding it to the header */}
            
            {/* Empty state */}
            {availableVariables.length === 0 && selectedVariables.length === 0 && (
              <div className="p-3 text-center text-gray-500">
                No variables available to select
              </div>
            )}
            
            {/* Max selections reached warning */}
            {isMaxReached && availableVariables.length > 0 && (
              <div className="p-2 bg-amber-50 text-amber-800 text-xs text-center border-t border-amber-100">
                {maxSelections === 1 
                  ? "Only one variable can be selected" 
                  : `Maximum of ${maxSelections} variables can be selected`}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}